import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useUploadAttachment } from "../../hooks/useAttachments";
import { ImageCropper } from "../ImageCropper";
import { extractPdfText } from "../../utils/pdfText";
import { hasReadableText } from "../../logic/invoice-detection";
import type { AttachmentDto } from "../../api/types";

export interface AttachmentUploadModalProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess: (newAttachment: AttachmentDto) => void;
}

export const AttachmentUploadModal = ({
  open,
  onClose,
  onUploadSuccess,
}: AttachmentUploadModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  // A PDF that carries no text layer. Nothing downstream can read it — invoice
  // auto-fill matches characters, and so would any text search — so say it once,
  // here, rather than letting it be discovered later. This modal is every
  // upload's entry point (Home, the invoice form, a transaction's receipt, the
  // attachments admin), so the warning is deliberately about the *file*, not
  // about invoices. Only ever a *warning*: a scan or a photo is sometimes the
  // only copy there is, and those are worth keeping.
  const [noTextLayer, setNoTextLayer] = useState(false);
  const [checkingText, setCheckingText] = useState(false);
  const uploadMutation = useUploadAttachment();
  const isUploading = uploadMutation.isPending;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setError(null);
    setNoTextLayer(false);

    if (selectedFile.type !== "application/pdf") {
      // Images have no text layer by definition — nothing to warn about.
      if (selectedFile.type.startsWith("image/")) setIsCropperOpen(true);
      return;
    }

    // Look before the cropper takes over the screen: once it opens, this modal
    // is hidden and there is nowhere left to show the warning.
    setCheckingText(true);
    try {
      if (hasReadableText(await extractPdfText(selectedFile))) {
        setIsCropperOpen(true);
      } else {
        setNoTextLayer(true);
      }
    } catch (err) {
      // Unreadable for some other reason (encrypted, damaged). Not our warning
      // to give — carry on and let the upload proceed as it always did.
      console.error("Could not check the PDF for a text layer", err);
      setIsCropperOpen(true);
    } finally {
      setCheckingText(false);
    }
  };

  const handleUpload = async (
    blobToUpload: Blob,
    filename: string,
    contentType: "image/webp" | "application/pdf",
    previewBlob?: Blob,
  ) => {
    setError(null);
    try {
      const created = await uploadMutation.mutateAsync({
        file: blobToUpload,
        filename,
        contentType,
        preview: previewBlob,
      });
      onUploadSuccess(created);
      setFile(null);
    } catch (e) {
      const err = e as { message?: string };
      setError(err.message || "Failed to upload attachment");
    }
  };

  const onConfirmDirectUpload = () => {
    if (file) {
      handleUpload(file, file.name, "application/pdf");
    }
  };

  return (
    <>
      <Dialog
        open={open && !isCropperOpen}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Attachment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {noTextLayer && (
              <Alert
                severity="warning"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setNoTextLayer(false);
                      setIsCropperOpen(true);
                    }}
                  >
                    Continue anyway
                  </Button>
                }
              >
                This PDF is a picture — there is no text in it, so nothing can
                read what it says. Auto-fill won't work on it, and it won't turn
                up in a text search. If it came from a “share” or a “print to
                PDF”, fetch the real download instead. Attach it as-is if the
                picture is all there is.
              </Alert>
            )}
            <Button
              variant="contained"
              component="label"
              disabled={checkingText}
            >
              {checkingText ? "Checking…" : "Select File"}
              <input
                type="file"
                hidden
                onChange={handleFileChange}
                accept="image/*,application/pdf"
              />
            </Button>
            {file &&
              !file.type.startsWith("image/") &&
              file.type !== "application/pdf" && (
                <Box>
                  <Typography variant="body1">Selected: {file.name}</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    ({(file.size / 1024).toFixed(1)} KB)
                  </Typography>
                </Box>
              )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            onClick={onConfirmDirectUpload}
            variant="contained"
            disabled={
              !file ||
              file.type.startsWith("image/") ||
              file.type === "application/pdf" ||
              isUploading
            }
          >
            {isUploading ? <CircularProgress size={24} /> : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
      <ImageCropper
        open={isCropperOpen}
        file={file}
        onClose={() => {
          setIsCropperOpen(false);
          setFile(null);
        }}
        onSave={(blob) => {
          setIsCropperOpen(false);
          if (file?.type === "application/pdf") {
            handleUpload(file, file.name, "application/pdf", blob);
          } else {
            const extIndex = file!.name.lastIndexOf(".");
            const baseName =
              extIndex !== -1 ? file!.name.substring(0, extIndex) : file!.name;
            // The cropped blob doubles as the preview — without one the
            // backend never bumps previewVersion, so the attachment would
            // show the "no preview" placeholder forever.
            handleUpload(blob, `${baseName}.webp`, "image/webp", blob);
          }
        }}
      />
    </>
  );
};
